import React, {useEffect} from 'react';
import {AddLarge, SettingsAdjust} from '@carbon/icons-react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import {
  useContentPaneHeight,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {AccountTier} from '../../shared/scoredAccounts';
import {useIsMyAccountsView, useUserControls} from './AccountGrid.controlstate';
import {ToggleFilterControl} from './FilterPopoverComponents/ToggleFilterControl';
import {FilterGroupControl} from './FilterPopoverComponents/FilterGroupControl';
import {useFilterGroup} from './FilterPopoverComponents/useFilterGroup';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ObjectId} from 'bson';

export const FilterControl = () => {
  const userControls = useUserControls();
  const {
    tiers,
    setTiers,
    excluded,
    setExcluded,
    disqualified,
    setDisqualified,
    filtersDifferFromDefault,
    numberOfActiveFiltersToDisplay,
    setTags,
  } = userControls;

  const isMyAccountsView = useIsMyAccountsView();

  // tiers
  const tierCheckboxes = _.map(tiers, (active: boolean, tier: AccountTier) => (
    <Checkbox
      key={tier}
      isChecked={active}
      onChange={(e) => setTiers(tier, e.target.checked)}
    >
      {tier}
    </Checkbox>
  ));

  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  const maxFilterPaneHeight = useContentPaneHeight();

  const activeFiltersCount = numberOfActiveFiltersToDisplay();

  const filterGroup = useFilterGroup();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    const tagParam = searchParams.get('tag');
    if (tagParam) {
      setTags([new ObjectId(tagParam)]);
      filterGroup.setFilters(['tags']);
      navigate({search: ''}, {replace: true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setTags, navigate]);

  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Button
          variant="ghost"
          color="kgray.400"
          backgroundColor={activeFiltersCount ? 'kgray.100' : undefined}
          fontSize="14"
          fontWeight="500"
          leftIcon={
            <Box>
              <SettingsAdjust />
            </Box>
          }
        >
          Filter
          {activeFiltersCount ? (
            <Box
              backgroundColor="kblue.400"
              borderRadius="999px"
              width="21px"
              height="21px"
              color="white"
              display="flex"
              justifyContent="center"
              alignItems="center"
              fontSize="14px"
              fontWeight="500"
              ml={2}
            >
              {activeFiltersCount}
            </Box>
          ) : null}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          fontSize={14}
          width="760px"
          maxHeight={maxFilterPaneHeight}
          overflowY="auto"
        >
          <PopoverHeader>
            <Flex justifyContent="space-between" alignItems="center" py={2}>
              <Text fontWeight={500} fontSize="lg">
                Show accounts where...
              </Text>

              {isMyAccountsView && !isFreeOrListBuilderCustomer && (
                <ToggleFilterControl
                  value={excluded ? 'excluded' : 'active'}
                  onChange={(value) => setExcluded(value === 'excluded')}
                  options={[
                    {value: 'active', label: 'Active'},
                    {value: 'excluded', label: 'Excluded'},
                  ]}
                />
              )}
              {!isMyAccountsView && !isFreeOrListBuilderCustomer && (
                <ToggleFilterControl
                  value={disqualified ? 'disqualified' : 'recommended'}
                  onChange={(value) =>
                    setDisqualified(value === 'disqualified')
                  }
                  options={[
                    {value: 'recommended', label: 'Recommended'},
                    {value: 'disqualified', label: 'Disqualified'},
                  ]}
                />
              )}
            </Flex>
          </PopoverHeader>
          <PopoverBody p={4} gap={4} display="flex" flexDirection="column">
            <Flex gap={2} alignItems="center">
              <Text width="48px">Tier is</Text>
              <Flex gap={6} alignItems="center">
                {tierCheckboxes}
              </Flex>
            </Flex>

            <FilterGroupControl filterGroup={filterGroup} />
          </PopoverBody>
          <PopoverFooter>
            <Flex justifyContent="space-between" alignItems="center">
              <Button
                size="md"
                variant="ghost"
                onClick={filterGroup.addFilterControl}
                leftIcon={<AddLarge />}
              >
                Add filter
              </Button>
              <Button
                size="md"
                variant="ghost"
                onClick={filterGroup.resetFilters}
                disabled={!filtersDifferFromDefault()}
              >
                Clear filters
              </Button>
            </Flex>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
